import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
  Chip,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Edit, Add } from '@mui/icons-material';
import { collection, query, where, getDocs, doc, getDoc, orderBy } from '../services/firestoreCompat';
import { db } from '../services/firebaseConfig';
import { useLanguage } from '../contexts/LanguageContext';

interface AddOn {
  id: string;
  name: string;
  name_de: string;
  price: number;
}

interface OrderItem {
  id?: string;
  menu_item_id?: string;
  menu_item_name?: string;
  name?: string;
  quantity: number;
  unit_price: number;
  addons?: string[];
  customizations?: any;
  notes?: string;
}

interface EditOrderItemModalProps {
  open: boolean;
  onClose: () => void;
  item: OrderItem | null;
  itemIndex: number;
  onSave: (updatedItem: OrderItem, itemIndex: number) => Promise<void>;
  language?: 'ar' | 'en';
}

const EditOrderItemModal: React.FC<EditOrderItemModalProps> = ({
  open,
  onClose,
  item,
  itemIndex,
  onSave,
  language = 'ar',
}) => {
  const { t } = useLanguage();
  const [availableAddOns, setAvailableAddOns] = useState<AddOn[]>([]);
  const [selectedAddOns, setSelectedAddOns] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (open && item) {
      loadAvailableAddOns();
      // Initialize selected add-ons from item
      if (item.addons && Array.isArray(item.addons)) {
        setSelectedAddOns(new Set(item.addons));
      } else {
        setSelectedAddOns(new Set());
      }
      setNotes(item.notes || '');
    }
  }, [open, item]);

  const loadAvailableAddOns = async () => {
    if (!item?.menu_item_id) {
      console.warn('⚠️ [EDIT ITEM] No menu_item_id found, trying to fetch all add-ons');
        // Fallback: fetch all add-ons
        try {
          setLoading(true);
          const addonsRef = collection(db, 'addons');
          // Try to order by sort_order, fallback to name if sort_order doesn't exist
          let snapshot;
          try {
            snapshot = await getDocs(query(addonsRef, where('is_active', '==', true), orderBy('sort_order'), orderBy('name')));
          } catch (err) {
            // If orderBy fails, just fetch without ordering
            snapshot = await getDocs(query(addonsRef, where('is_active', '==', true)));
          }
        const addons: AddOn[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          if (data.is_active !== false) {
            addons.push({
              id: doc.id,
              name: data.name || '',
              name_de: data.name_de || data.name || '',
              price: parseFloat(data.price || 0),
            });
          }
        });
        setAvailableAddOns(addons);
        console.log(`✅ [EDIT ITEM] Loaded ${addons.length} add-ons (all available)`);
      } catch (err: any) {
        console.error('❌ [EDIT ITEM] Error loading add-ons:', err);
        setError(t('orders.load_addons_failed'));
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // First, try to get menu_item_addons to find which add-ons are available for this item
      const menuItemAddOnsRef = collection(db, 'menu_item_addons');
      const q = query(menuItemAddOnsRef, where('menu_item_id', '==', item.menu_item_id));
      const snapshot = await getDocs(q);

      const addonIds: string[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        if (data.addon_id) {
          addonIds.push(data.addon_id);
        }
      });

      // If we found linked add-ons, fetch their details
      if (addonIds.length > 0) {
        const addons: AddOn[] = [];
        
        // Fetch each addon by ID
        for (const addonId of addonIds) {
          try {
            const addonDocRef = doc(db, 'addons', addonId);
            const addonDocSnap = await getDoc(addonDocRef);
            
            if (addonDocSnap.exists()) {
              const data = addonDocSnap.data();
              if (data.is_active !== false) {
                addons.push({
                  id: addonDocSnap.id,
                  name: data.name || '',
                  name_de: data.name_de || data.name || '',
                  price: parseFloat(data.price || 0),
                });
              }
            }
          } catch (err) {
            console.warn(`⚠️ [EDIT ITEM] Could not fetch addon ${addonId}:`, err);
          }
        }

        // If we couldn't fetch via menu_item_addons, fallback to all addons
        if (addons.length === 0) {
          console.log('⚠️ [EDIT ITEM] No linked add-ons found, fetching all add-ons');
          let allAddonsSnapshot;
          try {
            allAddonsSnapshot = await getDocs(query(collection(db, 'addons'), where('is_active', '==', true), orderBy('sort_order'), orderBy('name')));
          } catch (err) {
            allAddonsSnapshot = await getDocs(query(collection(db, 'addons'), where('is_active', '==', true)));
          }
          allAddonsSnapshot.forEach((doc) => {
            const data = doc.data();
            if (data.is_active !== false) {
              addons.push({
                id: doc.id,
                name: data.name || '',
                name_de: data.name_de || data.name || '',
                price: parseFloat(data.price || 0),
              });
            }
          });
        }

        setAvailableAddOns(addons);
        console.log(`✅ [EDIT ITEM] Loaded ${addons.length} available add-ons for item`);
      } else {
        // Fallback: fetch all active add-ons
        console.log('⚠️ [EDIT ITEM] No menu_item_addons found, fetching all add-ons');
        const addonsRef = collection(db, 'addons');
        let snapshot;
        try {
          snapshot = await getDocs(query(addonsRef, where('is_active', '==', true), orderBy('sort_order'), orderBy('name')));
        } catch (err) {
          snapshot = await getDocs(query(addonsRef, where('is_active', '==', true)));
        }
        const addons: AddOn[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          if (data.is_active !== false) {
            addons.push({
              id: doc.id,
              name: data.name || '',
              name_de: data.name_de || data.name || '',
              price: parseFloat(data.price || 0),
            });
          }
        });
        setAvailableAddOns(addons);
        console.log(`✅ [EDIT ITEM] Loaded ${addons.length} add-ons (all available)`);
      }
    } catch (err: any) {
      console.error('❌ [EDIT ITEM] Error loading add-ons:', err);
      setError('Failed to load add-ons. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddOnToggle = (addonId: string) => {
    const newSelected = new Set(selectedAddOns);
    if (newSelected.has(addonId)) {
      newSelected.delete(addonId);
    } else {
      newSelected.add(addonId);
    }
    setSelectedAddOns(newSelected);
  };

  const handleSave = async () => {
    if (!item) return;

    try {
      setLoading(true);
      setError(null);

      const updatedItem: OrderItem = {
        ...item,
        addons: Array.from(selectedAddOns),
        notes: notes.trim() || undefined,
      };

      await onSave(updatedItem, itemIndex);
      onClose();
    } catch (err: any) {
      console.error('❌ [EDIT ITEM] Error saving item:', err);
      setError(err.message || t('orders.save_changes_failed'));
    } finally {
      setLoading(false);
    }
  };

  if (!item) return null;

  const itemName = item.name || item.menu_item_name || 'Unknown Item';
  const selectedAddOnsList = Array.from(selectedAddOns)
    .map((id) => availableAddOns.find((a) => a.id === id))
    .filter((a): a is AddOn => a !== undefined);

  const totalAddOnPrice = selectedAddOnsList.reduce((sum, addon) => sum + addon.price, 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Edit sx={{ color: '#1976D2' }} />
          <Typography variant="h6">
            {t('addons.edit_item')} - {itemName}
          </Typography>
          <Chip
            label={`#${itemIndex + 1}`}
            size="small"
            color="primary"
            sx={{ ml: 'auto' }}
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            {itemName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('addons.quantity')}: {item.quantity}x |{' '}
            {t('addons.price')}: €{item.unit_price.toFixed(2)}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
          {t('addons.addons_extras')}
        </Typography>

        {loading && availableAddOns.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : availableAddOns.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {t('addons.no_addons_for_item')}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
            {availableAddOns.map((addon) => {
              const isSelected = selectedAddOns.has(addon.id);
              const displayName = language === 'ar' ? addon.name_de : addon.name;

              return (
                <Box
                  key={addon.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1.5,
                    border: `1px solid ${isSelected ? '#1976D2' : '#e0e0e0'}`,
                    borderRadius: 1,
                    backgroundColor: isSelected ? '#E3F2FD' : 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: '#1976D2',
                      backgroundColor: isSelected ? '#BBDEFB' : '#F5F5F5',
                    },
                  }}
                  onClick={() => handleAddOnToggle(addon.id)}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleAddOnToggle(addon.id)}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: isSelected ? 600 : 400 }}>
                          {displayName}
                        </Typography>
                      </Box>
                    }
                    sx={{ flex: 1, m: 0 }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976D2' }}>
                    +€{addon.price.toFixed(2)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}

        {selectedAddOnsList.length > 0 && (
          <Box sx={{ mb: 3, p: 2, backgroundColor: '#F5F5F5', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              {t('addons.selected_addons')}:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {selectedAddOnsList.map((addon) => {
                const displayName = language === 'ar' ? addon.name_de : addon.name;
                return (
                  <Chip
                    key={addon.id}
                    label={`${displayName} (+€${addon.price.toFixed(2)})`}
                    size="small"
                    color="primary"
                    onDelete={() => handleAddOnToggle(addon.id)}
                  />
                );
              })}
            </Box>
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
              {t('addons.total_addons')}: +€{totalAddOnPrice.toFixed(2)}
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        <TextField
          label={t('addons.notes_comments')}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          multiline
          rows={3}
          fullWidth
          placeholder={t('orders.additional_notes')}
        />
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <Add />}
        >
          {t('addons.save_changes')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditOrderItemModal;
